import { useEffect, useState, useRef } from "react";
import { Clock, LogIn, LogOut, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function getGeoStamp(timeoutMs = 6000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({});
      return;
    }
    let done = false;
    const finish = (val) => {
      if (done) return;
      done = true;
      resolve(val);
    };
    const t = setTimeout(() => finish({}), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(t);
        finish({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
        });
      },
      () => {
        clearTimeout(t);
        finish({});
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 }
    );
  });
}

function formatDuration(startIso) {
  const start = new Date(startIso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.floor((now - start) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * ClockButton — handles geolocation + clock-in/out for a given client.
 * @param {string} clientId
 * @param {object} active - the caregiver's currently active shift (or null)
 * @param {function} onChange - called after a successful clock action
 * @param {"compact"|"full"} variant
 */
export default function ClockButton({ clientId, active, onChange, variant = "compact" }) {
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const intervalRef = useRef(null);

  const isActiveForThis = active && active.client_id === clientId;
  const isActiveElsewhere = active && active.client_id !== clientId;

  useEffect(() => {
    if (isActiveForThis) {
      intervalRef.current = setInterval(() => setTick((n) => n + 1), 1000);
      return () => clearInterval(intervalRef.current);
    }
  }, [isActiveForThis]);

  const clockIn = async (e) => {
    e?.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const geo = await getGeoStamp();
      const { data } = await api.post(`/clients/${clientId}/clock-in`, geo);
      toast.success("Clocked in");
      onChange?.(data);
    } catch (err) {
      // Geofence rejections are descriptive (distance + required radius) — show longer
      const msg = err?.response?.data?.detail || "Clock in failed";
      const isGeofence = err?.response?.status === 403 && /from .* home|location required/i.test(msg);
      toast.error(msg, { duration: isGeofence ? 8000 : 4000 });
    } finally {
      setBusy(false);
    }
  };

  const clockOut = async (e) => {
    e?.stopPropagation();
    if (busy || !isActiveForThis) return;
    setBusy(true);
    try {
      const geo = await getGeoStamp();
      const { data } = await api.post(`/shifts/${active.id}/clock-out`, geo);
      toast.success(`Clocked out · ${data.duration_minutes} min`);
      onChange?.(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clock out failed");
    } finally {
      setBusy(false);
    }
  };

  if (isActiveElsewhere && variant === "compact") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-lg bg-clinical-bg px-2 py-1 text-[11px] text-clinical-subtext"
        title={`Clocked in at ${active.client_name}`}
        data-testid="clock-elsewhere-badge"
      >
        <Clock className="h-3 w-3" /> elsewhere
      </span>
    );
  }

  if (isActiveForThis) {
    void tick; // depend on tick for re-render
    if (variant === "compact") {
      return (
        <button
          onClick={clockOut}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-500/20"
          data-testid="clock-out-compact"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />}
          <span className="font-mono">{formatDuration(active.clock_in_at)}</span>
        </button>
      );
    }
    return (
      <div
        className="flex items-center justify-between gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/5 px-4 py-3"
        data-testid="clock-active-banner"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-700 animate-pulse-mic">
            <Clock className="h-4 w-4" />
          </span>
          <div className="leading-tight">
            <div className="text-xs text-clinical-subtext">On shift · live timer</div>
            <div className="font-heading font-semibold text-emerald-700 font-mono text-lg">
              {formatDuration(active.clock_in_at)}
            </div>
          </div>
        </div>
        <button
          onClick={clockOut}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
          data-testid="clock-out-button"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
          Clock out
        </button>
      </div>
    );
  }

  // Not clocked in for this client
  if (variant === "compact") {
    return (
      <button
        onClick={clockIn}
        disabled={busy || isActiveElsewhere}
        className="inline-flex items-center gap-1.5 rounded-lg border border-clinical-primary/30 bg-white px-2.5 py-1.5 text-xs font-medium text-clinical-primary transition-colors hover:border-clinical-primary/60 hover:bg-clinical-primary/5 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="clock-in-compact"
        title={isActiveElsewhere ? "Clock out first" : "Clock in here"}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogIn className="h-3 w-3" />}
        Clock in
      </button>
    );
  }
  return (
    <button
      onClick={clockIn}
      disabled={busy || isActiveElsewhere}
      className="btn-primary w-full"
      data-testid="clock-in-button"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          <MapPin className="h-4 w-4" /> Clock in here
        </>
      )}
    </button>
  );
}
